/**
 * D-EZ-109 — THE INJURY REPORT: WHO IS OUT NEXT WEEK, AND FOR HOW LONG.
 *
 * Read from what the season already booked through race N, and nothing after:
 *
 *   • who sat out race N and how long each had left going in (the race's own
 *     absence list, `SeasonRaceResult.absences`);
 *   • the clocks ticking down one race, exactly as the season ticks them
 *     (`seasonPlay.ts` `applySanctions`);
 *   • what race N's week and race added: a Race Week suspension (this race
 *     served first), a Race Week season-ending injury, a serious EZRA injury in
 *     the race (its "races missed" reading on the season ledger), the Super Rare
 *     career consequences (`careerConsequences.ts`), a fatality, a driver who
 *     quit to sign elsewhere.
 *
 * ⚠ IT IS A MIRROR, AND THE MIRROR IS CHECKED. This re-reads the engine's own
 * bookkeeping rather than inventing any, and `injuries.test.ts` holds it to the
 * engine: over seeded seasons, the list for "after race N" is exactly race
 * N+1's absence list — who, why and how many races left — and, for the live
 * season, exactly `SeasonState.carry`. The test looks ahead; this code never
 * does.
 */

import type { HistorySeason } from '../history/source.ts';
import type { SeasonRaceResult } from '../seasonPlay.ts';
import { SEASON_RACES } from '../schedule.ts';

export type OutKind = 'suspended' | 'injured' | 'between-rides';

export interface OutEntry {
  driverId: string;
  kind: OutKind;
  /** Races he still has to miss from race N+1 on (N+1 included), or the rest of the season. */
  racesLeft: number | 'season';
  /** Booked in race N's week or race (new news), rather than carried in. */
  isNew: boolean;
  /** The race the absence began at, when the season's books show it. */
  since: number | null;
}

export interface OutReport {
  /** After race N: who will not be on the entry list for race N+1. */
  out: OutEntry[];
  /** Sat out race N and is cleared to race again. */
  back: string[];
  /** Lost his life in race N's week or race. */
  died: { driverId: string; name: string; text: string }[];
}

/** "2 races of a suspension left", "1 race of recovery left", "out for the season", "between rides — 3 weeks until he signs". */
export function parseAbsenceNote(note: string): number | 'season' | null {
  if (/out for the season/i.test(note)) return 'season';
  const m = note.match(/(\d+)\s+(race|week)/i);
  return m ? Number(m[1]) : null;
}

/** EZRA's "races missed" reading, as the season ledger files it ("Misses 2 Races", "Misses Rest of Season"). */
export function parseRacesMissed(value: unknown): number | 'season' | null {
  if (typeof value !== 'string') return null;
  if (/rest of season/i.test(value)) return 'season';
  if (/next week/i.test(value)) return 0;
  const m = value.match(/(\d+)/);
  return m ? Number(m[1]) : null;
}

/**
 * The injury report after race `raceNumber` of `season` (a season cut at that
 * race or later — only races up to `raceNumber` are read).
 */
export function outAfterRace(season: HistorySeason, raceNumber: number): OutReport {
  const races = season.season.races.filter((r) => r.raceNumber <= raceNumber).sort((a, b) => a.raceNumber - b.raceNumber);
  const run = races.find((r) => r.raceNumber === raceNumber);
  if (!run) return { out: [], back: [], died: [] };

  // ---- the clocks going into race N, from race N's own absence list ------------
  const suspended = new Map<string, number>();
  const injured = new Map<string, number | 'season'>();
  const transfer = new Map<string, number>();
  for (const a of run.absences) {
    const left = parseAbsenceNote(a.note);
    if (left === null) continue;
    if (a.reason === 'suspended' && typeof left === 'number') suspended.set(a.driverId, left);
    else if (a.reason === 'injured' || a.reason === 'season-injury') injured.set(a.driverId, a.reason === 'season-injury' ? 'season' : left);
    else if (a.reason === 'quit' && typeof left === 'number') transfer.set(a.driverId, left);
  }
  const satOut = new Set([...suspended.keys(), ...injured.keys(), ...transfer.keys()]);

  // ---- race N is served: every clock comes down one (applySanctions) ----------
  for (const [id, left] of [...suspended]) {
    if (left - 1 > 0) suspended.set(id, left - 1);
    else suspended.delete(id);
  }
  for (const [id, left] of [...injured]) {
    if (left === 'season') continue;
    if (left - 1 > 0) injured.set(id, left - 1);
    else injured.delete(id);
  }
  for (const [id, left] of [...transfer]) transfer.set(id, left - 1);

  const fresh = new Set<string>();
  // ---- what race N's week booked ----------------------------------------------
  for (const s of run.week.suspensions ?? []) {
    const remaining = Math.max(0, s.races - 1);
    if (remaining > 0) {
      suspended.set(s.driverId, Math.max(suspended.get(s.driverId) ?? 0, remaining));
      fresh.add(s.driverId);
    }
  }
  for (const i of run.week.injuries ?? []) {
    if (i.duration === 'season') {
      injured.set(i.driverId, 'season');
      fresh.add(i.driverId);
    }
  }
  for (const t of run.week.teamTransfers ?? []) {
    transfer.set(t.driverId, Math.max(1, t.weeks));
    fresh.add(t.driverId);
  }

  // ---- what race N itself booked: serious EZRA injuries, then the Super Rare rows
  const dead = new Set<string>();
  const died: OutReport['died'] = [];
  for (const u of run.week.unableToRace ?? []) {
    if (u.reason === 'dead') dead.add(u.driverId);
  }
  for (const e of run.events ?? []) {
    if (e.momentTag !== 'season.injury') continue;
    const id = e.actorIds[0];
    const missed = parseRacesMissed(e.modifiers.find((m) => m.name === 'races missed')?.value);
    if (!id || missed === null) continue;
    if (missed === 'season') injured.set(id, 'season');
    else if (missed > 0) injured.set(id, missed);
    else continue;
    fresh.add(id);
  }
  for (const c of run.race.careerConsequences ?? []) {
    if (c.kind === 'fatality') {
      dead.add(c.driverId);
      injured.delete(c.driverId);
      suspended.delete(c.driverId);
      continue;
    }
    if (c.kind === 'injury') {
      const was = injured.get(c.driverId);
      injured.set(c.driverId, c.races === 'season' || was === 'season' ? 'season' : Math.max(was ?? 0, c.races));
    } else {
      const was = suspended.get(c.driverId) ?? 0;
      suspended.set(c.driverId, c.races === 'season' ? SEASON_RACES : Math.max(was, c.races));
    }
    fresh.add(c.driverId);
  }
  for (const f of season.season.fatalities ?? []) {
    if (f.raceNumber === raceNumber) {
      dead.add(f.driverId);
      died.push({ driverId: f.driverId, name: f.name, text: (f as { text?: string }).text ?? '' });
    }
  }
  for (const id of dead) {
    injured.delete(id);
    suspended.delete(id);
    transfer.delete(id);
  }

  const since = (id: string): number | null => sinceOf(races, id, raceNumber, fresh.has(id));
  const out: OutEntry[] = [];
  for (const [id, left] of suspended) out.push({ driverId: id, kind: 'suspended', racesLeft: left >= SEASON_RACES ? 'season' : left, isNew: fresh.has(id), since: since(id) });
  for (const [id, left] of injured) out.push({ driverId: id, kind: 'injured', racesLeft: left, isNew: fresh.has(id), since: since(id) });
  // A man between rides whose clock has run out signs before the next race (settleTransfers).
  for (const [id, left] of transfer) if (left > 0) out.push({ driverId: id, kind: 'between-rides', racesLeft: left, isNew: fresh.has(id), since: since(id) });
  out.sort((a, b) => Number(b.isNew) - Number(a.isNew) || rank(b.racesLeft) - rank(a.racesLeft) || a.driverId.localeCompare(b.driverId));

  const stillOut = new Set(out.map((o) => o.driverId));
  const back = [...satOut].filter((id) => !stillOut.has(id) && !dead.has(id)).sort();
  return { out, back, died };
}

const rank = (n: number | 'season'): number => (n === 'season' ? 1000 : n);

/** The first race of his current spell on the sidelines. */
function sinceOf(races: readonly SeasonRaceResult[], id: string, raceNumber: number, isNew: boolean): number | null {
  if (isNew) return raceNumber;
  let first: number | null = null;
  for (let i = races.length - 1; i >= 0; i--) {
    const r = races[i];
    if (r.raceNumber > raceNumber) continue;
    if (!r.absences.some((a) => a.driverId === id)) break;
    first = r.raceNumber;
  }
  return first;
}
